import React, { useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
} from '@mui/material';
import { Check, Edit2, Save, X } from 'lucide-react';
import { useToast } from '../layout/Layout';
import { useUserPermissions } from '../context/UserPermissionsContext';
import { listGroupsByDC, Group } from '../services/settingsServices/groupsService';
import {
    Capability,
    listCapabilities,
    getGroupPermissionsByDC,
    saveGroupPermissions,
} from '../services/settingsServices/capabilitiesService';
import { broadcastDCChange } from '../utils/dcEvents';
import { ALL_DCS } from '../utils/DCoption';
import './Permission.css';

interface GroupRow {
    groupId: string;
    groupNm: string;
    permissions: string[];
    loadingPerms: boolean;
}

const toGroupRow = (g: Group): GroupRow => ({
    groupId: g.groupId,
    groupNm: g.groupNm || g.groupId,
    permissions: [],
    loadingPerms: false,
});

const Permission = () => {
    const toast = useToast();
    const { isAdmin, userPermissions } = useUserPermissions();

    const [selectedDC, setSelectedDC] = useState(() => sessionStorage.getItem('selectedDC') || '');
    const [rows, setRows] = useState<GroupRow[]>([]);
    const [loadingGroups, setLoadingGroups] = useState(false);

    const dcList = useMemo(() => {
        if (isAdmin) return ALL_DCS;
        if (!userPermissions) return [];
        return Array.from(new Set(userPermissions.roles.map((r) => r.dcCode))).sort((a, b) => a.localeCompare(b));
    }, [isAdmin, userPermissions]);

    const [capabilities, setCapabilities] = useState<Capability[]>([]);
    const [loadingCaps, setLoadingCaps] = useState(false);

    // Inline edit
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [draftCaps, setDraftCaps] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);

    // Load capabilities list once on mount
    useEffect(() => {
        setLoadingCaps(true);
        listCapabilities()
            .then(setCapabilities)
            .catch(() => {})
            .finally(() => setLoadingCaps(false));
    }, []);

    // Sync selectedDC with the shared session key; default to first available DC if nothing stored
    useEffect(() => {
        if (dcList.length === 0) return;
        const stored = sessionStorage.getItem('selectedDC') || '';
        const initial = stored && dcList.includes(stored) ? stored : dcList[0];
        setSelectedDC(initial);
        sessionStorage.setItem('selectedDC', initial);
    }, [dcList]);

    // Phase 2: load groups for the selected DC, then fetch permissions for all groups in that DC
    useEffect(() => {
        if (!selectedDC) return;
        setLoadingGroups(true);

        Promise.all([
            listGroupsByDC(selectedDC),
            getGroupPermissionsByDC(selectedDC)
        ])
            .then(([groups, permissionsMap]) => {
                const baseRows = groups.map((g) => ({
                    ...toGroupRow(g),
                    permissions: permissionsMap[g.groupId] || [],
                    loadingPerms: false,
                }));
                setRows(baseRows);
            })
            .catch((err: any) => {
                const data = err?.response?.data;
                const message =
                    data?.message ||
                    data?.error ||
                    (typeof data === 'string' ? data : null) ||
                    err?.message ||
                    'Failed to load groups.';
                toast.showError(message);
            })
            .finally(() => setLoadingGroups(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDC]);

    const handleDCChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const dc = e.target.value;
        setSelectedDC(dc);
        sessionStorage.setItem('selectedDC', dc);
        broadcastDCChange(dc);
    };

    const [originalCaps, setOriginalCaps] = useState<Set<string>>(new Set());

    const openEdit = (groupId: string) => {
        const row = rows.find((r) => r.groupId === groupId);
        const perms = new Set(row?.permissions || []);
        setEditingGroupId(groupId);
        setDraftCaps(perms);
        setOriginalCaps(new Set(perms));
    };

    const isCapsChanged = useMemo(() => {
        if (draftCaps.size !== originalCaps.size) return true;
        for (const cap of draftCaps) { if (!originalCaps.has(cap)) return true; }
        return false;
    }, [draftCaps, originalCaps]);

    const toggleCap = (capId: string) => {
        setDraftCaps((prev) => {
            const next = new Set(prev);
            next.has(capId) ? next.delete(capId) : next.add(capId);
            return next;
        });
    };

    const handleSave = async () => {
        if (!editingGroupId) return;
        setSaving(true);
        try {
            const permissions = Array.from(draftCaps);
            await saveGroupPermissions(editingGroupId, permissions);

            // Update the permissions in the table
            setRows((prev) =>
                prev.map((r) =>
                    r.groupId === editingGroupId
                        ? { ...r, permissions }
                        : r
                )
            );

            const groupNm = rows.find((r) => r.groupId === editingGroupId)?.groupNm ?? editingGroupId;
            toast.showSuccess(`Permissions updated for "${groupNm}".`);
            setEditingGroupId(null);
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                data?.message ||
                data?.error ||
                (typeof data === 'string' ? data : null) ||
                err?.message ||
                'Failed to save permissions.';
            toast.showError(message);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditingGroupId(null);
        setDraftCaps(new Set());
    };

    const colSpanTotal = capabilities.length + 1 + (isAdmin ? 1 : 0); // group name + cap columns + action (admin only)

    return (
        <div className="perm-page">
            <div className="settings-title-row">
                <p className="settings-title">Permissions</p>
                <div className="settings-title-actions">
                    <select
                        value={selectedDC}
                        onChange={handleDCChange}
                        disabled={dcList.length === 0}
                        className={`customSelect ${dcList.length === 0 ? 'disabled' : 'enabled'}`}
                    >
                        {dcList.length === 0 && <option value="">No DC available</option>}
                        {dcList.map((dc) => (
                            <option key={dc} value={dc}>{dc}</option>
                        ))}
                    </select>
                    <span className="perm-count">{rows.length} groups</span>
                </div>
            </div>
            <div className="perm-card">
                <div className="perm-table-scroll">
                    <table className="perm-table perm-table-caps">
                        <thead>
                            <tr>
                                <th className="cap-group-col">Group</th>
                                {loadingCaps ? (
                                    <th colSpan={5} style={{ textAlign: 'center', color: '#98a2b3' }}>
                                        Loading capabilities…
                                    </th>
                                ) : (
                                    capabilities.map((cap) => (
                                        <th key={cap.hubCapabilityConfigId} className="cap-col">
                                            <span className="cap-col-nm">{cap.hubCapabilityNm}</span>
                                        </th>
                                    ))
                                )}
                                {isAdmin && <th className="action-col">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loadingGroups ? (
                                <tr>
                                    <td colSpan={colSpanTotal} className="perm-empty-cell">
                                        Loading groups…
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={colSpanTotal} className="perm-empty-cell">
                                        {selectedDC ? `No groups found for DC "${selectedDC}".` : 'Select a DC to view groups.'}
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <tr key={row.groupId}>
                                        <td className="cap-group-cell">
                                            <span className="group-name-cell">{row.groupNm}</span>
                                        </td>
                                        {capabilities.map((cap) => (
                                            <td key={cap.hubCapabilityConfigId} className="cap-mark-cell">
                                                {editingGroupId === row.groupId ? (
                                                    <Checkbox
                                                        checked={draftCaps.has(cap.hubCapabilityNm)}
                                                        onChange={() => toggleCap(cap.hubCapabilityNm)}
                                                        disabled={saving}
                                                        size="small"
                                                    />
                                                ) : row.loadingPerms ? (
                                                    <span className="cap-loading-dot" />
                                                ) : row.permissions.includes(cap.hubCapabilityNm) ? (
                                                    <span className="mark mark-yes" title="Access granted">
                                                        <Check size={13} strokeWidth={3} />
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#d0d5dd' }}>—</span>
                                                )}
                                            </td>
                                        ))}
                                        {isAdmin && <td className="cap-action-cell">
                                            {(
                                                editingGroupId === row.groupId ? (
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            type="button"
                                                            className="row-btn"
                                                            onClick={handleSave}
                                                            title="Save"
                                                            disabled={saving || !isCapsChanged}
                                                            style={{ color: isCapsChanged ? '#059669' : '#d0d5dd' }}
                                                        >
                                                            <Save size={15} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="row-btn"
                                                            onClick={handleCancel}
                                                            title="Cancel"
                                                            disabled={saving}
                                                            style={{ color: '#dc2626' }}
                                                        >
                                                            <X size={15} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="row-btn row-btn-capability"
                                                        onClick={() => openEdit(row.groupId)}
                                                        title="Edit capabilities"
                                                    >
                                                        <Edit2 size={15} />
                                                    </button>
                                                )
                                            )}
                                        </td>}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="perm-legend">
                    <div className="legend-item">
                        <span className="mark mark-yes"><Check size={12} strokeWidth={3} /></span> Available capability
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Permission;
